import { useMemo } from "react";
import { Trophy, Medal, Award, Building2, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { fmtInt, fmtPct, pct } from "@/lib/format";

const MEDALS = [
  { icon: Trophy, color: "text-amber-400", bg: "bg-amber-400/15", ring: "border-amber-400/40", label: "Ouro" },
  { icon: Medal, color: "text-slate-300", bg: "bg-slate-300/15", ring: "border-slate-300/40", label: "Prata" },
  { icon: Award, color: "text-orange-500", bg: "bg-orange-500/15", ring: "border-orange-500/40", label: "Bronze" },
];

export default function RankingConsultores({ condoConsultorMap = {}, monthLabel = "", onSelect = null }) {
  const podium = useMemo(() => {
    const arr = Object.entries(condoConsultorMap).map(([nome, v]) => ({
      nome,
      vendas: Number(v?.vendas || 0),
      meta: Number(v?.meta || 0),
      condominios: Number(v?.condominios || 0),
    }));
    arr.sort((a, b) => b.vendas - a.vendas);
    const top = arr.slice(0, 3);
    const max = top[0]?.vendas || 1;
    return top.map((v) => ({ ...v, barPct: (v.vendas / max) * 100 }));
  }, [condoConsultorMap]);

  if (podium.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-5" data-testid="ranking-consultores">
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Pódio dos consultores</h3>
        </div>
        <div className="text-sm text-muted-foreground">
          Atribua consultores aos condomínios para acompanhar o pódio de vendas por prédio.
        </div>
      </div>
    );
  }

  const [first, second, third] = [podium[0], podium[1], podium[2]];

  const Column = ({ v, place }) => {
    if (!v) return <div className="flex-1" />;
    const medal = MEDALS[place - 1];
    const Icon = medal.icon;
    const heights = { 1: "h-40", 2: "h-32", 3: "h-24" };
    const percent = pct(v.vendas, v.meta || 1);
    const clickable = typeof onSelect === "function";
    const Tag = clickable ? "button" : "div";
    return (
      <Tag
        type={clickable ? "button" : undefined}
        onClick={clickable ? () => onSelect(v.nome) : undefined}
        className={`flex-1 flex flex-col items-center gap-2 ${clickable ? "cursor-pointer hover:opacity-90 active:scale-[0.98] transition" : ""}`}
        data-testid={`podio-${place}`}
      >
        <div className={`w-12 h-12 rounded-full grid place-items-center ${medal.bg} border ${medal.ring} shadow-lg`}>
          <Icon className={`w-6 h-6 ${medal.color}`} />
        </div>
        <div className="text-center">
          <div className="font-heading font-bold truncate max-w-[140px]" title={v.nome}>{v.nome}</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{medal.label}</div>
        </div>
        <div
          className={`w-full ${heights[place]} rounded-t-lg ${medal.bg} border-t-2 border-l border-r ${medal.ring} flex flex-col items-center justify-start pt-3 gap-1`}
        >
          <span className={`font-heading text-3xl font-extrabold tabular-nums ${medal.color}`}>{fmtInt(v.vendas)}</span>
          <span className="text-[10px] text-muted-foreground">vendas</span>
          {v.meta > 0 && (
            <Badge variant="outline" className={`text-[9px] mt-1 ${percent >= 100 ? "text-emerald-500 border-emerald-500/40" : "text-muted-foreground"}`}>
              {fmtPct(percent)} da meta
            </Badge>
          )}
          <span className="text-[10px] text-muted-foreground mt-auto mb-2 flex items-center gap-1">
            <Building2 className="w-2.5 h-2.5" /> {v.condominios} prédio{v.condominios !== 1 ? "s" : ""}
          </span>
        </div>
      </Tag>
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="ranking-consultores">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Pódio dos consultores</h3>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            <Users className="w-3 h-3 mr-1" /> Vendas em condomínios
          </Badge>
          <Badge variant="outline">{monthLabel || "Todos os meses"}</Badge>
        </div>
      </div>
      {/* Pódio: 2º | 1º | 3º */}
      <div className="flex items-end gap-3 md:gap-6 max-w-2xl mx-auto">
        <Column v={second} place={2} />
        <Column v={first} place={1} />
        <Column v={third} place={3} />
      </div>
      {/* Detalhes tabulares */}
      <div className="mt-6 space-y-2">
        {podium.map((v, idx) => (
          <div key={v.nome} className="flex items-center justify-between text-xs border-t border-border pt-2">
            <div className="flex items-center gap-2">
              <span className="w-6 text-center font-mono text-muted-foreground">{idx + 1}º</span>
              <span className="font-medium">{v.nome}</span>
            </div>
            <div className="flex items-center gap-3 tabular-nums">
              <span className="text-muted-foreground">{v.condominios} prédio(s)</span>
              <span className="font-mono">{fmtInt(v.vendas)}{v.meta > 0 ? `/${fmtInt(v.meta)}` : ""}</span>
              <div className="w-24"><Progress value={v.barPct} className="h-1.5" /></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
